import { useEffect, useState, type ReactElement } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { TwilioRoomMessage, TwilioRoom } from '../../components/organisms/TwilioRoom/TwilioRoom';
import { UserService } from '../../services';
import { Icon } from '../../components/atoms/Icon/Icon';
import { Button } from '../../components/atoms/Button/Button';
import { PatientNotesOverlay } from '../../components/organisms/PatientNotesOverlay/PatientNotesOverlay';

export function AppointmentRoom(): ReactElement {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  if (typeof appointmentId !== 'string') {
    throw new Error('appointmentId param is must be string');
  }

  const navigate = useNavigate();

  const [name, setName] = useState<string>();
  const [token, setToken] = useState<string>();
  const [error, setError] = useState<string>('');

  const [patientNotes, setPatientNotes] = useState<string>('');
  const [isNotesExpanded, setIsNotesExpanded] = useState<boolean>(true);

  useEffect(() => {
    const numericAppointmentId = parseInt(appointmentId, 10);
    if (isNaN(numericAppointmentId)) {
      setError('Ungültige Termin-ID.');
      return;
    }

    const fetchData = async () => {
      try {
        const appointmentRes = await UserService.getAppointmentData(numericAppointmentId);
        if (appointmentRes.error || !appointmentRes.data) {
          toast.error(appointmentRes.message || 'Termindetails konnten nicht geladen werden.');
          throw new Error('Failed to fetch appointment data');
        }
        setPatientNotes(appointmentRes.data.patientNotes || '');

        const tokenRes = await UserService.getTwilioDoctorAppointmentToken(appointmentId);
        setToken(tokenRes.data.data.jwt);
        setName(tokenRes.data.data.room);
      } catch (err) {
        console.error(err);
        setError('Fehler bei der Initialisierung des Raumes.');
      }
    };

    fetchData();
  }, [appointmentId]);

  const connectingMessage = 'Warten, bis das Zimmer bereit ist...';
  const waitingMessage = 'Ihr Patient wird in wenigen Augenblicken hier sein...';

  return (
    <div
      style={{
        backgroundColor: '#111',
        color: 'white',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
      }}
    >
      <header
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          padding: '1rem',
          width: '100%',
          display: 'flex',
          zIndex: 20,
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Icon icon='logo' size={200} color='white' />
        <Button onClick={() => navigate('/')} style={{ display: 'inline-block', margin: '0', padding: '0.5rem' }}>
          Anruf beenden
        </Button>
      </header>
      <div
        style={{
          paddingTop: '3rem',
          height: 'calc(100vh - 3rem)',
          position: 'relative',
        }}
      >
        {error ? (
          <TwilioRoomMessage message={error} />
        ) : name && token ? (
          <TwilioRoom name={name} token={token} waitingMessage={waitingMessage} connectingMessage={connectingMessage} />
        ) : (
          <TwilioRoomMessage message={connectingMessage} />
        )}

        <PatientNotesOverlay
          notes={patientNotes}
          isExpanded={isNotesExpanded}
          onToggleExpand={() => setIsNotesExpanded(!isNotesExpanded)}
        />
      </div>
    </div>
  );
}
